import type {
  AnalyticsConnection,
  AnalyticsMonthlyVisits,
  AnalyticsPageMetric,
  AnalyticsSnapshot
} from "./analyticsQueries";
import { isClosedStatus, serviceEndedAt, type ClientRequest, type PortalService } from "./requestQueries";
import type { PortalIncident, PortalMonitor } from "./statusQueries";

/**
 * What Översikt shows, as arithmetic, since build round 4 (2026-09-27).
 *
 * The overview became the client's dashboard with the sketch Mattej approved on 2026-09-26
 * (`prototype/kund/pages.js` `overviewPage`, plan.md section 5.1): boxes that show their content
 * instead of a row per area that links on. Everything a box decides is here, apart from the drawing,
 * so it can be tested without a browser and without a database.
 *
 * Three rules from the sketch decide most of it. A number is shown in one place. What is normal is a
 * grey word or nothing. And an empty state is quiet: a box with nothing to say is not drawn, rather
 * than drawn with a sentence explaining that it is empty.
 */

/* --- Behöver er uppmärksamhet, and Ärenden ------------------------------------------------------ */

const newestFirst = (a: ClientRequest, b: ClientRequest) => b.updated_at.localeCompare(a.updated_at);

/**
 * The requests waiting for the client, newest first. Only requests: plan.md section 5.1 since
 * 2026-09-26, "the ärenden waiting for the client's answer, each with its title. Nothing else".
 * Notification text is never read here; a notice says something happened, a request in this status
 * is something the client still has to do.
 */
export function waitingOnClient(requests: ClientRequest[]): ClientRequest[] {
  return requests.filter((request) => request.status === "waiting_for_client").sort(newestFirst);
}

/** The open requests not already under Uppmärksamhet, newest first, at most five. */
export const OTHER_OPEN_LIMIT = 5;

/**
 * Build round A1, 2026-10-08: "open" leaves out both closed statuses, Klart and Avböjt, through
 * `isClosedStatus`, so a declined request is never counted as one still being worked on.
 */
export function otherOpenRequests(requests: ClientRequest[]): ClientRequest[] {
  return requests
    .filter((request) => !isClosedStatus(request.status) && request.status !== "waiting_for_client")
    .sort(newestFirst)
    .slice(0, OTHER_OPEN_LIMIT);
}

/* --- Besök --------------------------------------------------------------------------------------- */

const monthShort = new Intl.DateTimeFormat("sv-SE", { month: "short" });
const monthLong = new Intl.DateTimeFormat("sv-SE", { month: "long", year: "numeric" });

export interface MonthBar {
  /** `YYYY-MM-01`, the first day of the month. */
  month: string;
  /** "maj", for the label under the bar. Without the dot `Intl` puts after an abbreviation. */
  short: string;
  /** "maj 2026", or "september 2026, hittills" for the month in progress, for a screen reader. */
  long: string;
  /** Null when nothing is stored for the month: not measured, which is not zero. */
  visits: number | null;
  current: boolean;
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
}

/**
 * Twelve bars, the month in progress last. A month with no stored total is an empty slot and not a
 * zero bar: the history starts the month analytics was connected, and a month before that was not
 * measured (decision 0387).
 */
export function monthBars(
  rows: AnalyticsMonthlyVisits[],
  connectionId: string,
  now: Date = new Date()
): MonthBar[] {
  const stored = new Map(
    rows
      .filter((row) => row.analytics_connection_id === connectionId)
      .map((row) => [row.month.slice(0, 10), row.visits])
  );

  const bars: MonthBar[] = [];
  for (let back = 11; back >= 0; back -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - back, 1);
    const key = monthKey(date);
    const current = back === 0;
    bars.push({
      month: key,
      // Three letters, as the sketch draws them: `Intl` writes "mars", "juni" and "juli" in full.
      short: monthShort.format(date).replace(/\.$/, "").slice(0, 3),
      long: current ? `${monthLong.format(date)}, hittills` : monthLong.format(date),
      visits: stored.has(key) ? (stored.get(key) as number) : null,
      current
    });
  }
  return bars;
}

export interface VisitChange {
  percent: number;
  direction: "more" | "fewer" | "same";
}

/**
 * The 30 days against the 30 before, as a whole percent. Null when there is nothing to compare with,
 * which is a connection younger than 60 days or a previous period with no visits: a percent of zero
 * is not a number anybody can read.
 */
export function visitChange(visits: number, previous: number | null): VisitChange | null {
  if (previous === null || previous <= 0) {
    return null;
  }
  const percent = Math.round((Math.abs(visits - previous) / previous) * 100);
  if (percent === 0) {
    return { percent: 0, direction: "same" };
  }
  return { percent, direction: visits > previous ? "more" : "fewer" };
}

export function changeSentence(change: VisitChange): string {
  if (change.direction === "same") {
    return "Lika många som de 30 dagarna innan";
  }
  return `${change.percent} % ${change.direction === "more" ? "fler" : "färre"} än de 30 dagarna innan`;
}

/* --- Varifrån besökarna kommer ------------------------------------------------------------------- */

export interface SourceShare {
  label: string;
  percent: number;
}

/** Five rows at most: the four largest and Övriga, or all of them when there are five or fewer. */
export const SOURCE_ROWS = 5;

/**
 * The shares of the stored sources, largest first. A null host is a visit with no referrer, which is
 * "Direkt" (the Hint says what that means). The percentages are of the sources the Worker stored, the
 * ten largest, so Övriga is the rest of those and not of every visit.
 */
export function sourceShares(sources: Array<{ host: string | null; visits: number }>): SourceShare[] {
  const sorted = sources.filter((source) => source.visits > 0).slice().sort((a, b) => b.visits - a.visits);
  const total = sorted.reduce((sum, source) => sum + source.visits, 0);
  if (total === 0) {
    return [];
  }

  const head = sorted.length > SOURCE_ROWS ? sorted.slice(0, SOURCE_ROWS - 1) : sorted;
  const rest = sorted.length > SOURCE_ROWS ? sorted.slice(SOURCE_ROWS - 1) : [];
  const share = (visits: number) => Math.round((visits / total) * 100);

  const rows = head.map((source) => ({ label: source.host ?? "Direkt", percent: share(source.visits) }));
  if (rest.length > 0) {
    rows.push({ label: "Övriga", percent: share(rest.reduce((sum, source) => sum + source.visits, 0)) });
  }
  return rows;
}

/* --- one connection's figures -------------------------------------------------------------------- */

/**
 * Mest besökta sidor shows five rows at most, since 2026-09-27. The Worker still stores up to 25 per
 * snapshot, because that is what the database permits and the display is the only thing narrowed.
 * The rows arrive ordered by `rank`, so the first five are the five most visited.
 */
export const PAGE_ROWS = 5;

export interface VisitsView {
  connection: AnalyticsConnection;
  serviceName: string | null;
  /** Null when the connection has no current figures: never succeeded, failing, or the cache expired. */
  snapshot: AnalyticsSnapshot | null;
  /** At most `PAGE_ROWS`, the most visited first. */
  pages: AnalyticsPageMetric[];
}

/** One view per connection, each with its newest snapshot and that snapshot's pages. */
export function visitsViews(
  connections: AnalyticsConnection[],
  snapshots: AnalyticsSnapshot[],
  pages: AnalyticsPageMetric[],
  services: PortalService[]
): VisitsView[] {
  const nameById = new Map(services.map((service) => [service.id, service.display_name]));
  return connections
    .filter((connection) => connection.status !== "disabled" && connection.status !== "revoked")
    .map((connection) => {
      // Ordered by `period_end` then `captured_at`, both descending, so the first match is the newest.
      const snapshot = snapshots.find((row) => row.analytics_connection_id === connection.id) ?? null;
      return {
        connection,
        serviceName: nameById.get(connection.service_id) ?? null,
        snapshot,
        pages: snapshot
          ? pages
              .filter((page) => page.analytics_snapshot_id === snapshot.id)
              .sort((left, right) => left.rank - right.rank)
              .slice(0, PAGE_ROWS)
          : []
      };
    });
}

/* --- Webbplatsen ----------------------------------------------------------------------------------- */

export const SPEED_WORD: Record<string, string> = {
  good: "Bra",
  needs_improvement: "Okej",
  poor: "Långsam"
};

/**
 * The cause in the client's words, short. The long sentences `/status` carried are the same four
 * judgements; the box has one line for them. `unknown` still says that nobody has determined it,
 * because a blank is read as the worst answer (architecture.md section 12).
 */
export const CAUSE_SHORT: Record<string, string> = {
  petrovic: "Vårt fel.",
  third_party: "Leverantörens fel.",
  client_environment: "Orsaken låg i er egen miljö.",
  unknown: "Orsaken är inte fastställd än."
};

export function causeLine(incident: Pick<PortalIncident, "cause_attribution" | "client_summary">): string {
  const cause = CAUSE_SHORT[incident.cause_attribution] ?? "";
  const summary = incident.client_summary?.trim() ?? "";
  return [cause, summary].filter(Boolean).join(" ");
}

export interface SiteRow {
  serviceId: string;
  name: string | null;
  /** Null when nothing about this service is checked: no active monitor, or a state not known. */
  state: "up" | "down" | null;
  /** When a confirmed outage began, for "Nere sedan". */
  downSince: string | null;
  /** The confirmed outage's cause, shown at once (plan.md section 5.6, 2026-09-26). */
  cause: string | null;
  speed: string | null;
  /**
   * Build round 46, 2026-10-17: when the service was archived. Such a row says "Avslutad" and nothing
   * else, because its monitors are retired and its speed is no longer read.
   */
  endedAt: string | null;
}

/**
 * One row per service that is watched or measured, and one per archived service.
 *
 * Down means a monitor is `unavailable`, which the dispatcher sets only after the retry failed, so a
 * possible problem is Uppe until it is confirmed: plan.md section 5.6, "the retry above exists so that
 * one failed check reaches nobody". A switched-off monitor is not checked and says nothing, and a
 * state this build does not know says nothing rather than Uppe.
 */
export function siteRows(
  services: PortalService[],
  monitors: PortalMonitor[],
  incidents: PortalIncident[],
  views: VisitsView[]
): SiteRow[] {
  const rows: SiteRow[] = [];

  for (const service of services) {
    const endedAt = serviceEndedAt(service);
    if (endedAt) {
      rows.push({ serviceId: service.id, name: service.display_name, state: null, downSince: null, cause: null, speed: null, endedAt });
      continue;
    }

    const active = monitors.filter((monitor) => monitor.service_id === service.id && monitor.active);
    const speedRating = views.find((view) => view.connection.service_id === service.id)?.snapshot?.speed_rating ?? null;
    const speed = speedRating ? (SPEED_WORD[speedRating] ?? null) : null;

    if (active.length === 0 && !speed) {
      continue;
    }

    const down = active.some((monitor) => monitor.current_state === "unavailable");
    const known = active.every((monitor) =>
      ["available", "possible_problem", "unavailable"].includes(monitor.current_state)
    );
    const state = active.length === 0 ? null : down ? "down" : known ? "up" : null;

    // The open confirmed incident, newest first as the read returns them.
    const open = down
      ? incidents.find(
          (incident) =>
            incident.service_id === service.id && incident.status === "confirmed" && incident.recovered_at === null
        )
      : undefined;

    rows.push({
      serviceId: service.id,
      name: service.display_name,
      state,
      downSince: open ? (open.confirmed_at ?? open.detected_at) : null,
      cause: open ? causeLine(open) : null,
      speed,
      endedAt: null
    });
  }

  return rows;
}

/** How far back the latest disruption is shown, plan.md section 5.6. */
export const DISRUPTION_DAYS = 90;

export interface Disruption {
  incident: PortalIncident;
  serviceName: string | null;
  /** "12 minuter", "3 timmar", "2 dygn". */
  duration: string | null;
}

export function durationWords(from: string, to: string): string | null {
  const ms = new Date(to).getTime() - new Date(from).getTime();
  if (!Number.isFinite(ms) || ms < 0) {
    return null;
  }
  const minutes = Math.max(1, Math.round(ms / 60_000));
  if (minutes < 60) {
    return minutes === 1 ? "1 minut" : `${minutes} minuter`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return hours === 1 ? "1 timme" : `${hours} timmar`;
  }
  return `${Math.round(hours / 24)} dygn`;
}

/**
 * The newest confirmed incident that is over and began within 90 days. A possible incident is not a
 * disruption (it never reached anybody), and one still going is said on its service's row instead.
 */
export function lastDisruption(
  incidents: PortalIncident[],
  services: PortalService[],
  now: Date = new Date()
): Disruption | null {
  const cutoff = now.getTime() - DISRUPTION_DAYS * 24 * 60 * 60 * 1000;
  const nameById = new Map(services.map((service) => [service.id, service.display_name]));

  const found = incidents
    .filter(
      (incident) =>
        incident.confirmed_at !== null &&
        incident.recovered_at !== null &&
        new Date(incident.detected_at).getTime() >= cutoff
    )
    .sort((a, b) => b.detected_at.localeCompare(a.detected_at))[0];

  if (!found) {
    return null;
  }

  return {
    incident: found,
    serviceName: nameById.get(found.service_id) ?? null,
    duration: durationWords(found.detected_at, found.recovered_at as string)
  };
}

/**
 * When every watched address was last looked at: the oldest of the active monitors' latest checks,
 * so the one line stays true for each row under it.
 */
export function checkedAt(monitors: PortalMonitor[]): string | null {
  let oldest: { value: string; time: number } | null = null;
  for (const monitor of monitors) {
    if (!monitor.active || !monitor.last_checked_at) {
      continue;
    }
    const time = new Date(monitor.last_checked_at).getTime();
    if (Number.isNaN(time)) {
      continue;
    }
    if (!oldest || time < oldest.time) {
      oldest = { value: monitor.last_checked_at, time };
    }
  }
  return oldest?.value ?? null;
}
